import { useState, useEffect, useRef } from 'react';
import { OrganizationalUnit } from '../../models/org-units/orgUnit';
import { AppPage, AppCard } from '../../components/general';
import OrgUnitsService from '../../services/orgUnitsService';
import BrokerSyncService from '../../services/brokerSyncService';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import { Tree } from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Messages } from 'primereact/messages';
import { InputText } from 'primereact/inputtext';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { TabView, TabPanel } from 'primereact/tabview';
import EditOrgUnit from './EditOrgUnit';
import './OrganizationalUnits.css';
import { OrganizationalUnitUsers } from './OrganizationalUnitUsers';
import OrgUnitCompensations from './OrgUnitCompensations';
import ConnectionSettings from '../connection-settings/ConnectionSettings';

const orgUnitsService = OrgUnitsService.getInstance();
const brokerSyncService = BrokerSyncService.getInstance();

const OrganizationalUnits = () => {
  const toast = useRef<Toast>(null);
  const errors = useRef<Messages>(null);
  const treeRef = useRef<any>(null);

  const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);
  const [filteredNodes, setFilteredNodes] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrganizationalUnit | undefined>(undefined);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [selectedNodeForDetails, setSelectedNodeForDetails] = useState<OrganizationalUnit | undefined>(undefined);
  const [selectedParentId, setSelectedParentId] = useState<string | undefined>(undefined);
  const [searchValue, setSearchValue] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string | undefined>(undefined);

  useEffect(() => {
    loadOrgUnits();
  }, []);

  const loadOrgUnits = () => {
    setLoading(true);
    orgUnitsService
      .getHierarchy()
      .then((orgUnits) => {
        const nodes = buildTreeFromOrgUnits(orgUnits);
        setTreeNodes(nodes);
      })
      .catch((error: Error) => showError(error.message))
      .finally(() => setLoading(false));
  };

  // Transform list of org units into a tree structure
  const buildTreeFromOrgUnits = (units: OrganizationalUnit[]): TreeNode[] => {
    // Build a map of all units by id for quick lookups
    const unitsMap = new Map<string, OrganizationalUnit>();
    units.forEach((unit) => {
      unitsMap.set(unit.orgUnitId, unit);
    });

    // Find root nodes (units with no parent or parent not in our list)
    const rootNodes: TreeNode[] = [];

    units.forEach((unit) => {
      // Check if this is a root node (no parent or parent not in our list)
      if (!unit.parentOrgUnitId || !unitsMap.has(unit.parentOrgUnitId)) {
        rootNodes.push(createTreeNode(unit, unitsMap));
      }
    });

    return rootNodes;
  };

  const createTreeNode = (unit: OrganizationalUnit, unitsMap: Map<string, OrganizationalUnit>): TreeNode => {
    const node: TreeNode = {
      key: unit.orgUnitId,
      label: unit.name,
      data: unit,
      children: [],
    };

    // Add child nodes if any
    if (unit.children && unit.children.length > 0) {
      // Children are now full objects, not just IDs
      unit.children.forEach((childUnit: OrganizationalUnit) => {
        node.children!.push(createTreeNode(childUnit, unitsMap));
      });
    }
    return node;
  };

  const handleAddNew = () => {
    setSelectedOrgUnit(undefined);
    setSelectedParentId(undefined);
    setSidebarVisible(true);
  };

  const handleNodeSelect = (e: any) => {
    if (e.node && e.node.data) {
      // Set the selected node for the details panel
      setSelectedNodeForDetails(e.node.data);
      setSelectedKeys(e.node.key as string);
    }
  };

  const handleAddChild = (unit: OrganizationalUnit) => {
    setSelectedOrgUnit(undefined);
    setSelectedParentId(unit.orgUnitId);
    setSidebarVisible(true);
  };

  const handleEditUnit = (unit: OrganizationalUnit) => {
    setSelectedOrgUnit(unit);
    setSelectedParentId(undefined);
    setSidebarVisible(true);
  };

  const handleSaveOrgUnit = (orgUnit: OrganizationalUnit) => {
    // Show confirmation dialog before saving
    confirmDialog({
      message: orgUnit.orgUnitId ? 'Are you sure you want to save these changes?' : 'Create this organizational unit?',
      header: orgUnit.orgUnitId ? 'Update Confirmation' : 'Create Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-primary',
      accept: () => {
        // If user confirms, proceed with the API call
        setApiLoading(true);
        console.log('Confirmation accepted, proceeding with API call');

        // Determine if this is an update or create operation
        const isUpdate = !!orgUnit.orgUnitId;

        const operation = isUpdate ? orgUnitsService.update(orgUnit) : orgUnitsService.create(orgUnit);

        operation
          .then(() => {
            // Reload the org units to reflect changes
            loadOrgUnits();
            setSidebarVisible(false);
            // Clear the selection after save to avoid showing stale data
            setSelectedNodeForDetails(undefined);
            setSelectedKeys(undefined);

            if (toast.current) {
              toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: `Organization unit ${isUpdate ? 'updated' : 'created'} successfully.`,
                life: 3000,
              });
            }
          })
          .catch((error: Error) => {
            // Show error toast and ensure we clear loading states
            if (toast.current) {
              toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || `Failed to ${isUpdate ? 'update' : 'create'} organization unit.`,
                life: 5000,
              });
            }
            console.error('Error saving organization unit:', error);
          })
          .finally(() => {
            setApiLoading(false);
          });
      },
      reject: () => {},
    });
  };

  const handleDeleteUnit = (unit: OrganizationalUnit) => {
    confirmDialog({
      message: `Are you sure you want to delete organizational unit: ${unit.name}?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        setLoading(true);
        orgUnitsService
          .delete(unit.orgUnitId)
          .then(() => {
            loadOrgUnits();
            setSelectedNodeForDetails(undefined);
          })
          .catch((error: Error) => showError(error.message))
          .finally(() => setLoading(false));
      },
    });
  };

  const handleSync = (syncType: 'PushToLOS' | 'PullFromLOS') => {
    setApiLoading(true);
    brokerSyncService
      .syncWithLos(syncType)
      .then((result) => {
        loadOrgUnits();
        if (toast.current) {
          toast.current.show({
            severity: 'success',
            summary: 'Success',
            detail: `Successfully ${syncType === 'PushToLOS' ? 'pushed to' : 'pulled from'} LOS. ${result.createdCount} records processed.`,
            life: 3000,
          });
        }
      })
      .catch((error: Error) => {
        if (toast.current) {
          toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: error.message || `Failed to ${syncType === 'PushToLOS' ? 'push to' : 'pull from'} LOS.`,
            life: 5000,
          });
        }
        console.error('Error syncing with LOS:', error);
      })
      .finally(() => {
        setApiLoading(false);
      });
  };

  const showError = (error: string) => {
    if (errors.current) {
      errors.current.show({ severity: 'error', summary: 'Error:', detail: error, life: 10000 });
    }
  };

  const emptyTemplate = () => {
    return (
      <div className="flex flex-column align-items-center py-5">
        <i className="pi pi-folder-open text-3xl text-gray-400 mb-3"></i>
        <span className="text-gray-600">No organizational units available</span>
      </div>
    );
  };

  const nodeTemplate = (node: TreeNode) => {
    const unit = node.data as OrganizationalUnit;

    return (
      <div className="flex align-items-center justify-content-between w-full">
        <span className="org-unit-label">{node.label}</span>
        <div className="flex">
          <Button
            icon="pi pi-plus"
            className="p-button-rounded p-button-text p-button-sm"
            tooltip="Add Child"
            tooltipOptions={{ position: 'top', showDelay: 500, hideDelay: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleAddChild(unit);
            }}
          />
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text p-button-sm"
            tooltip="Edit"
            tooltipOptions={{ position: 'top', showDelay: 500, hideDelay: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleEditUnit(unit);
            }}
          />
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-text p-button-danger p-button-sm"
            tooltip="Delete"
            tooltipOptions={{ position: 'top', showDelay: 500, hideDelay: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteUnit(unit);
            }}
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (treeNodes.length > 0) {
      if (searchValue.trim() === '') {
        setFilteredNodes([...treeNodes]);
      } else {
        // Custom filtering logic
        const filter = (nodes: TreeNode[]): TreeNode[] => {
          return nodes.reduce((filtered: TreeNode[], node) => {
            // Create a copy of the node
            const copy = { ...node };

            // Check if the node's label matches the search string
            const matchesLabel = copy.label?.toString().toLowerCase().includes(searchValue.toLowerCase());

            // If the node has children, filter them too
            if (copy.children && copy.children.length) {
              copy.children = filter(copy.children);
            }

            // Include the node if it matches or any of its children match
            if (matchesLabel || (copy.children && copy.children.length > 0)) {
              filtered.push(copy);
            }

            return filtered;
          }, []);
        };

        setFilteredNodes(filter([...treeNodes]));
      }
    } else {
      setFilteredNodes([]);
    }
  }, [treeNodes, searchValue]);

  return (
    <AppPage title="Organizational Units" breadcrumbs="Home - Organizational Units">
      <Toast ref={toast} />
      <ConfirmDialog />
      <Messages ref={errors} />
      <div className="!grid gap-5">
        <AppCard>
          {/* Header with title and actions */}
          <div className="flex justify-content-between align-items-center mb-4">
            <div className="font-semibold text-gray-800 text-[1.3rem]">Organizational Structure</div>
            <div className="flex gap-2 align-items-center">
              {treeNodes.length > 0 && (
                <span className="p-input-icon-left p-input-filled">
                  <i className="pi pi-search" />
                  <InputText
                    placeholder="Search"
                    className="min-w-[200px]"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                </span>
              )}
              <Button
                label="Push to LOS"
                icon="pi pi-upload"
                loading={apiLoading}
                onClick={() => handleSync('PushToLOS')}
                className="mr-2"
              />
              <Button
                label="Pull from LOS"
                icon="pi pi-download"
                loading={apiLoading}
                onClick={() => handleSync('PullFromLOS')}
                className="mr-2"
              />
              <Button label="Add New" icon="pi pi-plus" onClick={handleAddNew} />
            </div>
          </div>

          <Splitter style={{ height: 'calc(100vh - 200px)' }}>
            <SplitterPanel className="flex align-items-center justify-content-center" size={30} minSize={20}>
              <div className="w-full h-full overflow-auto">
                <Tree
                  ref={treeRef}
                  value={filteredNodes}
                  loading={loading}
                  emptyMessage={emptyTemplate}
                  className="org-unit-tree"
                  nodeTemplate={nodeTemplate}
                  onSelect={handleNodeSelect}
                  selectionMode="single"
                  selectionKeys={selectedKeys}
                  onSelectionChange={(e) => setSelectedKeys(e.value as string)}
                />
              </div>
            </SplitterPanel>
            <SplitterPanel className="flex align-items-start justify-content-center" size={70}>
              <div className="w-full h-full overflow-auto">
                {selectedNodeForDetails ? (
                  <div className="h-full">
                    <h2 className="text-xl font-semibold p-3 pb-0 mb-2">{selectedNodeForDetails.name}</h2>

                    <TabView>
                      <TabPanel header="Settings">
                        <EditOrgUnit
                          orgUnit={selectedNodeForDetails}
                          onSave={handleSaveOrgUnit}
                          onHide={() => {}}
                          apiLoading={apiLoading}
                        />
                      </TabPanel>
                      <TabPanel header="Connections">
                        <ConnectionSettings orgUnitId={selectedNodeForDetails.orgUnitId} asPage={false} />
                      </TabPanel>
                      <TabPanel header="Users">
                        <OrganizationalUnitUsers orgUnit={selectedNodeForDetails} selectedOrgUnit={selectedNodeForDetails} />
                      </TabPanel>
                      <TabPanel header="Compensation">
                        <OrgUnitCompensations orgUnitId={selectedNodeForDetails.orgUnitId} asPage={false} />
                      </TabPanel>
                    </TabView>
                  </div>
                ) : (
                  <div className="flex flex-column align-items-center justify-content-center h-full">
                    <i className="pi pi-info-circle text-3xl text-gray-400 mb-3"></i>
                    <span className="text-gray-600">Select an organizational unit to view details</span>
                  </div>
                )}
              </div>
            </SplitterPanel>
          </Splitter>
        </AppCard>
      </div>

      <Sidebar
        visible={sidebarVisible}
        position="right"
        onHide={() => {
          setSidebarVisible(false);
          // Also reset API loading state when sidebar is closed
          setApiLoading(false);
        }}
        className="max-lg:w-full lg:w-1/3"
        dismissable={false}
        closeIcon={null}
        header={selectedOrgUnit ? 'Edit Organizational Unit' : 'Add Organizational Unit'}
        style={{ overflow: 'hidden', height: '100vh', top: '0', bottom: '0' }}
      >
        {/* Custom overlay for the entire sidebar when loading */}
        {apiLoading && (
          <div
            className="absolute flex align-items-center justify-content-center"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1000,
            }}
          >
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
          </div>
        )}

        <EditOrgUnit
          onHide={() => setSidebarVisible(false)}
          onSave={handleSaveOrgUnit}
          orgUnit={selectedOrgUnit}
          parentOrgUnitId={selectedParentId}
          apiLoading={apiLoading}
        />
      </Sidebar>
    </AppPage>
  );
};

export default OrganizationalUnits;
